// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useContext } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    setPersistence,
    browserLocalPersistence,
    inMemoryPersistence
} from 'firebase/auth';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, storage } from '../firebase';
import { globalAppId } from '../utils/constants';
import { getPaths } from '../firebase.paths';
import { subscribeTermsConfig, TERMS_DEFAULT_VERSION } from '../services/termsService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// ─── Configuração de Sessão ────────────────────────────────────────────────
// Tempo máximo de inatividade antes de forçar logout (8 horas em ms)
const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const SESSION_KEY = 'rplay_last_activity';

const stampActivity = () => {
    try { localStorage.setItem(SESSION_KEY, String(Date.now())); } catch { /* noop */ }
};

const isSessionExpired = () => {
    try {
        const last = parseInt(localStorage.getItem(SESSION_KEY) || '0', 10);
        if (!last) return false; // primeira vez — deixa passar, carimba no login
        return Date.now() - last > SESSION_TIMEOUT_MS;
    } catch {
        return false;
    }
};

const clearSession = () => {
    try { localStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
};

// Eventos que contam como "atividade do usuário"
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const activityTimerRef = useRef(null);

    // ─── Config dos Termos (versionamento) ────────────────────────────────────
    const [termsConfig, setTermsConfig] = useState(null);
    const [termsConfigLoading, setTermsConfigLoading] = useState(true);

    const getUserPath = (uid) => `artifacts/${globalAppId}/public/data/users/${uid}`;

    // ─── Logout ────────────────────────────────────────────────────────────
    const handleLogout = async () => {
        clearSession();
        try {
            await signOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
        } catch (err) {
            console.error(err);
        }
    };

    // ─── Verificação de inatividade ────────────────────────────────────────
    // Debounce: atualiza o timestamp no máximo a cada 30 segundos para não
    // impactar performance com writes no localStorage a cada evento.
    const scheduleActivityCheck = () => {
        if (activityTimerRef.current) return;
        activityTimerRef.current = setTimeout(() => {
            activityTimerRef.current = null;
            stampActivity();
        }, 30_000);
    };

    // ─── Assinatura da config de termos (versionamento) ──────────────────────
    useEffect(() => {
        const paths = getPaths(globalAppId);
        const unsub = subscribeTermsConfig(db, paths.config, (config) => {
            setTermsConfig(config);
            setTermsConfigLoading(false);
        });
        return () => unsub();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Aceite de Termos (LGPD) ──────────────────────────────────────────
    const handleAcceptTerms = async () => {
        if (!currentUser) return;
        try {
            const version = termsConfig?.version || TERMS_DEFAULT_VERSION;
            await updateDoc(doc(db, getUserPath(currentUser.uid)), {
                termsAcceptedAt: serverTimestamp(),
                termsVersion: version,
            });
        } catch (err) {
            console.error('[handleAcceptTerms]', err);
            throw err;
        }
    };

    // ─── Login ─────────────────────────────────────────────────────────────
    const handleLogin = async (email, password) => {
        setError('');
        try {
            await setPersistence(auth, browserLocalPersistence)
                .catch(() => setPersistence(auth, inMemoryPersistence));
            await signInWithEmailAndPassword(auth, email, password);
            stampActivity(); // carimba imediatamente no login
        } catch (err) {
            setError(err.message || 'Erro ao fazer login.');
            throw err;
        }
    };

    // ─── Listener de autenticação ──────────────────────────────────────────
    useEffect(() => {
        let unsubscribeProfile = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Verifica expiração de sessão ao restaurar sessão salva
                if (isSessionExpired()) {
                    await signOut(auth);
                    clearSession();
                    setIsLoading(false);
                    return;
                }

                setCurrentUser(user);

                unsubscribeProfile = onSnapshot(doc(db, getUserPath(user.uid)), (docSnap) => {
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setUserProfile({
                            id: user.uid,
                            ...data,
                            role: Number(data.role)
                        });
                    }
                }, (err) => {
                    console.error("Erro ao sincronizar perfil:", err);
                });

            } else {
                setCurrentUser(null);
                setUserProfile(null);
                if (unsubscribeProfile) unsubscribeProfile();
            }
            setIsLoading(false);
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeProfile) unsubscribeProfile();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Detecção de atividade do usuário ─────────────────────────────────
    useEffect(() => {
        if (!currentUser) return;

        // Registra atividade nos principais eventos de interação
        ACTIVITY_EVENTS.forEach(evt =>
            window.addEventListener(evt, scheduleActivityCheck, { passive: true })
        );

        // Verifica expiração a cada minuto enquanto o app está aberto
        const intervalId = setInterval(() => {
            if (isSessionExpired()) handleLogout();
        }, 60_000);

        return () => {
            ACTIVITY_EVENTS.forEach(evt =>
                window.removeEventListener(evt, scheduleActivityCheck)
            );
            clearInterval(intervalId);
            if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
        };
    }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Lógica de aceite com suporte a versionamento ─────────────────────────
    // Enquanto a config dos termos ainda carrega, NÃO assume aceito.
    // O App.js usa termsConfigLoading para exibir um loading em vez do app/modal.
    // Após carregar:
    //   • sem versão configurada → usa lógica legada (termsAcceptedAt)
    //   • com versão configurada → compara termsVersion do usuário com a atual
    const termsAccepted = termsConfigLoading
        ? false
        : !termsConfig?.version
            ? !!userProfile?.termsAcceptedAt
            : userProfile?.termsVersion === termsConfig?.version;

    const value = {
        currentUser,
        userProfile,
        isAuthenticated: !!currentUser,
        termsAccepted,
        termsConfig,
        termsConfigLoading,
        userRole: userProfile?.role || 0,
        isLoading,
        error,
        handleLogin,
        handleLogout,
        handleAcceptTerms,
        auth, db, storage
    };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};
